/** @format */

import type { PlaybackMode, PlaybackState } from "@/context/playbackContext";

export type CacheScope =
  | "live"
  | {
      mode: PlaybackMode;
      lap: number | null;
    };

export interface PlaybackMetadata {
  isPlayback?: boolean;
  lap?: number | null;
  lapStart?: number | null;
  lapEnd?: number | null;
  lapProgress?: number | null;
  timestamp?: number | null;
  mode?: PlaybackMode;
}

const deriveMode = (
  lap: number | null,
  timestamp: number | null,
  isPlayback?: boolean,
  preferred?: PlaybackMode
): PlaybackMode => {
  if (preferred) {
    return preferred;
  }
  if (timestamp !== null) {
    return "timeline";
  }
  if (lap !== null) {
    return "lap";
  }
  if (isPlayback) {
    return "lap";
  }
  return "live";
};

const scopeFromValues = (
  values: PlaybackMetadata & { mode?: PlaybackMode }
): CacheScope => {
  const lap = values.lap ?? null;
  const timestamp = values.timestamp ?? null;
  const mode = deriveMode(lap, timestamp, values.isPlayback, values.mode);

  if (mode === "live") {
    return "live";
  }

  return {
    mode,
    lap,
  };
};

export const scopeFromPlaybackState = (state: PlaybackState): CacheScope =>
  scopeFromValues(state);

export const scopeFromMetadata = (metadata?: PlaybackMetadata): CacheScope => {
  if (!metadata) {
    return "live";
  }
  return scopeFromValues(metadata);
};

export const isLiveScope = (scope: CacheScope): scope is "live" => scope === "live";
